// @mui material components
import React from "react";
import Container from "@mui/material/Container";
import Grid from "@mui/material/Grid";
import Divider from "@mui/material/Divider";
import Card from "@mui/material/Card";

// Senthil Solar React components
import MKBox from "components/MKBox";
import MKTypography from "components/MKTypography";

// Senthil Solar React examples
import DefaultNavbar from "examples/Navbars/DefaultNavbar";
import DefaultFooter from "examples/Footers/DefaultFooter";

// Icons
import SecurityIcon from "@mui/icons-material/Security";
import NaturePeopleIcon from "@mui/icons-material/NaturePeople";
import BuildIcon from "@mui/icons-material/Build";
import TimerIcon from "@mui/icons-material/Timer";

// Routes
import routes from "routes";
import footerRoutes from "footer.routes";

// Images - Use an existing image or add 'streetlight_bg.jpg' to src/assets/images/
// For now, reusing 'canva_banner.png' from Awards as a placeholder; replace with a streetlight themed bg
import bgImage from "assets/images/canva_banner.png";

// Animation imports
import { useSpring, animated, useTrail } from 'react-spring';
import { useInView } from 'react-intersection-observer';

// Create animated versions of MUI components
const AnimatedGrid = animated(Grid);
const AnimatedMKBox = animated(MKBox);

function StreetLightService() {
  // For header animation
  const [headerRef, headerInView] = useInView({
    triggerOnce: false,
    threshold: 0.1,
  });
  
  const headerAnimation = useSpring({
    opacity: headerInView ? 1 : 0,
    transform: headerInView ? 'translateY(0)' : 'translateY(-50px)',
    config: { mass: 1, tension: 280, friction: 60 }
  });

  // For key benefits section
  const [benefitsRef, benefitsInView] = useInView({
    triggerOnce: false,
    threshold: 0.2,
  });

  const benefitsHeaderAnimation = useSpring({
    opacity: benefitsInView ? 1 : 0,
    transform: benefitsInView ? 'translateY(0)' : 'translateY(30px)',
    config: { mass: 1, tension: 280, friction: 60 }
  });

  const advantages = [
    {
      icon: <SecurityIcon />,
      title: "Enhanced Safety",
      description: "Bright, consistent lighting improves security and reduces crime in public spaces."
    },
    {
      icon: <NaturePeopleIcon />,
      title: "Eco-Friendly",
      description: "Zero carbon emissions, no grid dependency, completely renewable energy powered."
    },
    {
      icon: <BuildIcon />,
      title: "Easy Installation",
      description: "No trenching, no wiring, no grid connection required. Quick setup in any location."
    },
    {
      icon: <TimerIcon />,
      title: "Intelligent Control",
      description: "Auto on/off with dusk-to-dawn sensors, motion detection, and time-based dimming."
    }
  ];

  const [advantagesRef, advantagesInView] = useInView({
    triggerOnce: false,
    threshold: 0.1,
  });

  const advantagesTrail = useTrail(advantages.length, {
    opacity: advantagesInView ? 1 : 0,
    transform: advantagesInView ? 'translateY(0) scale(1)' : 'translateY(50px) scale(0.95)',
    config: { mass: 1, tension: 280, friction: 50 },
  });

  // For product range section
  const [productsRef, productsInView] = useInView({
    triggerOnce: false,
    threshold: 0.2,
  });
  
  const productsAnimation = useSpring({
    opacity: productsInView ? 1 : 0,
    transform: productsInView ? 'translateY(0)' : 'translateY(50px)',
    config: { mass: 1, tension: 280, friction: 60 }
  });

  const productTypes = [
    {
      title: "All-in-One Solar Street Light",
      power: "12W - 60W",
      features: "Integrated design, compact, easy installation, suitable for residential streets and pathways."
    },
    {
      title: "Split Solar Street Light",
      power: "30W - 120W",
      features: "Separate panel and light, higher capacity, ideal for highways and main roads."
    },
    {
      title: "Solar High Mast Light",
      power: "150W - 400W",
      features: "For large areas, parking lots, stadiums, and industrial complexes."
    }
  ];

  const [productsItemsRef, productsItemsInView] = useInView({
    triggerOnce: false,
    threshold: 0.1,
  });

  const productsTrail = useTrail(productTypes.length, {
    opacity: productsItemsInView ? 1 : 0,
    transform: productsItemsInView ? 'scale(1)' : 'scale(0.8)',
    config: { mass: 1, tension: 280, friction: 50 },
  });

  // For ideal for section
  const [idealRef, idealInView] = useInView({
    triggerOnce: false,
    threshold: 0.2,
  });
  
  const idealAnimation = useSpring({
    opacity: idealInView ? 1 : 0,
    transform: idealInView ? 'translateY(0)' : 'translateY(50px)',
    config: { mass: 1, tension: 280, friction: 60 }
  });

  const locations = [
    "Village Roads & Pathways",
    "Urban Streets & Colonies",
    "Parks & Gardens",
    "Industrial Areas",
    "Campus & Institutions",
    "Parking Areas",
    "Highways & Toll Plazas",
    "Bus Stops & Public Spaces"
  ];

  const [locationsRef, locationsInView] = useInView({
    triggerOnce: false,
    threshold: 0.1,
  });

  const locationsTrail = useTrail(locations.length, {
    opacity: locationsInView ? 1 : 0,
    transform: locationsInView ? 'scale(1)' : 'scale(0.8)',
    config: { mass: 1, tension: 280, friction: 50 },
  });

  // For smart features section
  const [smartRef, smartInView] = useInView({
    triggerOnce: false,
    threshold: 0.2,
  });
  
  const smartAnimation = useSpring({
    opacity: smartInView ? 1 : 0,
    transform: smartInView ? 'translateX(0)' : 'translateX(-50px)',
    config: { mass: 1, tension: 280, friction: 60 }
  });

  return (
    <>
      <DefaultNavbar
        routes={routes}
        transparent
        light
      />
      <MKBox
        minHeight="60vh"
        width="100%"
        sx={{
          backgroundImage: `linear-gradient(rgba(0, 0, 0, 0.6), rgba(0, 0, 0, 0.6)), url(${bgImage})`,
          backgroundSize: "cover",
          backgroundPosition: "center",
          display: "grid",
          placeItems: "center",
        }}
      >
        <Container sx={{ mt: 6 }}>
  <AnimatedGrid
    ref={headerRef}
    style={headerAnimation}
    container
    item
    xs={12}
    lg={7}
    justifyContent="center"
    mx="auto"
  >
    {/* First line */}
    <MKTypography
      variant="h1"
      color="white"
      mt={-6}
      mb={1}
      sx={({ breakpoints, typography: { size } }) => ({
        textAlign: "center",
        [breakpoints.down("sm")]: { fontSize: size["xl"] },
        [breakpoints.between("sm", "md")]: { fontSize: size["2xl"] },
        [breakpoints.up("md")]: { fontSize: size["3xl"] },
      })}
    >
      Solar Street Lights
    </MKTypography>

    {/* Second line */}
    <MKTypography
      variant="h5"
      color="white"
      textAlign="center"
      sx={({ breakpoints, typography: { size } }) => ({
        opacity: 0.9,
        [breakpoints.down("sm")]: { fontSize: size["md"] },
        [breakpoints.between("sm", "md")]: { fontSize: size["lg"] },
        [breakpoints.up("md")]: { fontSize: size["xl"] },
      })}
    >
      Illuminate Streets, Parks & Pathways with Clean Energy
    </MKTypography>
  </AnimatedGrid>
</Container>
      </MKBox>
      <Card
        sx={{
          p: 2,
          mx: { xs: 2, lg: 3 },
          mt: -8,
          mb: 4,
          backgroundColor: ({ palette: { white }, functions: { rgba } }) => rgba(white.main, 0.8),
          backdropFilter: "saturate(200%) blur(30px)",
          boxShadow: ({ boxShadows: { xxl } }) => xxl,
        }}
      >
        <MKBox component="section" py={6}>
          <Container>
            <Grid container item xs={12} lg={10} mx="auto" mb={4}>
              <MKTypography variant="body1" color="text" textAlign="center">
                Transform your community lighting with our advanced solar street light systems. 
                Designed for Indian conditions, our lights provide reliable illumination throughout 
                the night, even during monsoon season. With integrated lithium batteries and 
                intelligent controllers, our solar street lights offer 10+ hours of backup and 
                5+ years of maintenance-free operation.
              </MKTypography>
            </Grid>
            
            {/* Key Benefits Section */}
            <AnimatedGrid 
              ref={benefitsRef}
              style={benefitsHeaderAnimation}
              container 
              item 
              xs={12} 
              justifyContent="center" 
              mx="auto" 
              mb={4}
            >
              <MKTypography variant="h4" textAlign="center" mb={4}>
                Key Benefits
              </MKTypography>
            </AnimatedGrid>

            <Grid container spacing={3} ref={advantagesRef}>
              {advantagesTrail.map((style, index) => (
                <Grid item xs={12} md={6} key={index}>
                  <AnimatedMKBox 
                    style={style}
                    p={3} 
                    borderRadius="lg"
                    sx={{ 
                      backgroundColor: "rgba(255, 152, 0, 0.05)",
                      border: "1px solid rgba(255, 152, 0, 0.2)",
                      height: "100%"
                    }}
                  >
                    <MKBox display="flex" alignItems="center" mb={2}>
                      <MKBox color="warning.main" mr={2}>
                        {React.cloneElement(advantages[index].icon, { fontSize: "large" })}
                      </MKBox>
                      <MKTypography variant="h5">
                        {advantages[index].title}
                      </MKTypography>
                    </MKBox>
                    <MKTypography variant="body2" color="text">
                      {advantages[index].description}
                    </MKTypography>
                  </AnimatedMKBox>
                </Grid>
              ))}
            </Grid>

            <Divider sx={{ my: 5 }} />

            {/* Product Range Section */}
            <AnimatedGrid 
              ref={productsRef}
              style={productsAnimation}
              container 
              item 
              xs={12} 
              justifyContent="center" 
              mx="auto" 
              mt={6}
            >
              <MKTypography variant="h4" textAlign="center" mb={4}>
                Product Range
              </MKTypography>
            </AnimatedGrid>

            <Grid container spacing={3} ref={productsItemsRef}>
              {productsTrail.map((style, index) => (
                <Grid item xs={12} lg={8} key={index} mx="auto">
                  <AnimatedMKBox 
                    style={style}
                    mb={3} 
                    p={3} 
                    borderRadius="lg"
                    sx={{ backgroundColor: "rgba(0, 0, 0, 0.02)" }}
                  >
                    <Grid container spacing={2} alignItems="center">
                      <Grid item xs={12} md={4}>
                        <MKTypography variant="h5" color="warning.main">
                          {productTypes[index].title}
                        </MKTypography>
                        <MKTypography variant="button" color="text">
                          Power Range: {productTypes[index].power}
                        </MKTypography>
                      </Grid>
                      <Grid item xs={12} md={8}>
                        <MKTypography variant="body2" color="text">
                          {productTypes[index].features}
                        </MKTypography>
                      </Grid>
                    </Grid>
                  </AnimatedMKBox>
                </Grid>
              ))}
            </Grid>

            {/* Ideal For Section */}
            <AnimatedGrid 
              ref={idealRef}
              style={idealAnimation}
              container 
              item 
              xs={12} 
              md={10} 
              lg={8} 
              justifyContent="center" 
              mx="auto" 
              mt={6}
            >
              <MKTypography variant="h4" textAlign="center" mb={3}>
                Ideal For
              </MKTypography>
            </AnimatedGrid>

            <Grid container spacing={2} ref={locationsRef}>
              {locationsTrail.map((style, index) => (
                <Grid item xs={6} md={3} key={index}>
                  <animated.div style={style}>
                    <MKBox 
                      p={2} 
                      textAlign="center" 
                      borderRadius="lg"
                      sx={{ 
                        backgroundColor: "rgba(255, 152, 0, 0.1)",
                        border: "1px solid rgba(255, 152, 0, 0.3)",
                        transition: "transform 0.3s ease-in-out",
                        "&:hover": {
                          transform: "translateY(-5px)",
                        },
                      }}
                    >
                      <MKTypography variant="button" fontWeight="medium">
                        {locations[index]}
                      </MKTypography>
                    </MKBox>
                  </animated.div>
                </Grid>
              ))}
            </Grid>

            {/* Smart Features Section */}
            <AnimatedMKBox 
              ref={smartRef}
              style={smartAnimation}
              component="section" 
              p={4} 
              borderRadius="lg" 
              mb={5}
              mt={6}
              sx={{ backgroundColor: "rgba(76, 175, 80, 0.05)" }}
            >
              <MKTypography variant="h5" mb={2}>
                Smart Features Included
              </MKTypography>
              <MKTypography variant="body1" color="text">
                All our solar street lights come with automatic dusk-to-dawn operation, motion 
                sensor capability for energy optimization, time-based dimming profiles, remote 
                monitoring options, IP65 waterproof rating, and wind-resistant design suitable 
                for speeds up to 120 km/h. Backed by 3-year comprehensive warranty and 
                lifetime technical support.
              </MKTypography>
            </AnimatedMKBox>
          </Container>
        </MKBox>
      </Card>
      <MKBox pt={6} px={1} mt={6}>
        <DefaultFooter content={footerRoutes} />
      </MKBox>
    </>
  );
}

export default StreetLightService;